import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Sparkles, CreditCard, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { Button } from "../components/ui/button";

const PLAN_LABELS = { free: "Free", collector: "Collector Pass" };

export default function Billing() {
  const { user, checkAuth } = useAuth();
  const currencyCtx = useCurrency();
  const format = currencyCtx?.format || ((usd) => `$${Number(usd).toFixed(2)}`);
  const currency = currencyCtx?.currency || "USD";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [packages, setPackages] = useState(null);
  const [buyingId, setBuyingId] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    api.get("/billing/packages").then((res) => setPackages(res.data));
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;

    setConfirming(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/billing/checkout/${sessionId}/status`);
        if (res.data.payment_status === "paid") {
          toast.success("Payment confirmed! Your credits have been added.");
          setConfirming(false);
          await checkAuth();
          setSearchParams({});
          return;
        }
        if (res.data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          setConfirming(false);
          setSearchParams({});
          return;
        }
        if (attempts < 5) {
          setTimeout(poll, 2000);
        } else {
          toast.info("Still confirming your payment — check back in a moment.");
          setConfirming(false);
          setSearchParams({});
        }
      } catch {
        setConfirming(false);
        setSearchParams({});
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleBuy = async (packageId) => {
    setBuyingId(packageId);
    try {
      const res = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch {
      toast.error("Couldn't start checkout. Please try again.");
      setBuyingId(null);
    }
  };

  const planLabel = PLAN_LABELS[user?.plan] || "Free";

  return (
    <div className="min-h-screen bg-[#05060f] text-white px-6 py-16">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-display text-3xl md:text-4xl font-bold mb-2">Billing &amp; Credits</h1>
        <p className="text-zinc-400 text-sm md:text-base mb-10">
          Each card scanned or re-scanned with AI uses 0.25 credits. Re-pricing an existing card is always free.
        </p>

        {confirming && (
          <div data-testid="billing-confirming-banner" className="mb-8 flex items-center gap-3 border border-[#A78BFA]/30 bg-[#A78BFA]/5 rounded-xl p-4 text-sm text-[#A78BFA]">
            <Loader2 className="w-4 h-4 animate-spin" /> Confirming your payment...
          </div>
        )}

        <div data-testid="billing-current-plan" className="border border-white/10 bg-[#111113] rounded-2xl p-6 mb-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Current plan</p>
            <p className="text-xl font-display font-bold">{planLabel}</p>
            {user?.plan === "collector" && user?.plan_expires_at && (
              <p className="text-xs text-zinc-500 mt-1">Renews / expires {new Date(user.plan_expires_at).toLocaleDateString()}</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Credits remaining</p>
            <p data-testid="billing-credits-balance" className="text-3xl font-mono-plex font-bold text-[#A78BFA]">{user?.unlimited_credits ? "∞" : (user?.credits_balance ?? "—")}</p>
          </div>
        </div>

        {!packages ? (
          <div className="w-8 h-8 border-2 border-white/20 border-t-[#A78BFA] rounded-full animate-spin mx-auto" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div data-testid="billing-package-collector_pass" className="border border-[#A78BFA]/30 bg-gradient-to-b from-[#A78BFA]/10 to-transparent rounded-2xl p-8 flex flex-col">
              <Sparkles className="w-8 h-8 text-[#A78BFA] mb-4" />
              <h3 className="font-display text-xl font-bold mb-1">Collector Pass</h3>
              <p data-testid="billing-collector-price" className="text-3xl font-mono-plex font-bold mb-1">{format(packages.collector_pass.amount)}</p>
              {currency !== "USD" && (
                <p className="text-[10px] text-zinc-500 mb-1">billed as ${packages.collector_pass.amount.toFixed(2)} USD</p>
              )}
              <p className="text-xs text-zinc-500 mb-6">{packages.collector_pass.credits} credits · {packages.collector_pass.days}-day pass</p>
              <p className="text-sm text-zinc-400 mb-6 flex-1">
                Best for a big collection session — scan hundreds of cards without worrying about running out.
              </p>
              <Button
                data-testid="billing-buy-collector_pass-button"
                onClick={() => handleBuy("collector_pass")}
                disabled={buyingId === "collector_pass"}
                className="bg-[#7C3AED] hover:bg-[#6D28D9] rounded-full py-6 disabled:opacity-50"
              >
                <CreditCard className="w-4 h-4 mr-2" /> {buyingId === "collector_pass" ? "Redirecting..." : "Get Collector Pass"}
              </Button>
            </div>

            <div data-testid="billing-package-credit_topup" className="border border-white/10 bg-[#111113] rounded-2xl p-8 flex flex-col">
              <Zap className="w-8 h-8 text-[#F59E0B] mb-4" />
              <h3 className="font-display text-xl font-bold mb-1">Credit Top-up</h3>
              <p data-testid="billing-topup-price" className="text-3xl font-mono-plex font-bold mb-1">{format(packages.credit_topup.amount)}</p>
              {currency !== "USD" && (
                <p className="text-[10px] text-zinc-500 mb-1">billed as ${packages.credit_topup.amount.toFixed(2)} USD</p>
              )}
              <p className="text-xs text-zinc-500 mb-6">{packages.credit_topup.credits} credits · never expires</p>
              <p className="text-sm text-zinc-400 mb-6 flex-1">
                Ran out mid-scan? Top up instantly on top of your free or Collector Pass balance.
              </p>
              <Button
                data-testid="billing-buy-credit_topup-button"
                onClick={() => handleBuy("credit_topup")}
                disabled={buyingId === "credit_topup"}
                variant="outline"
                className="border-white/20 hover:bg-white/5 rounded-full py-6 disabled:opacity-50"
              >
                <CreditCard className="w-4 h-4 mr-2" /> {buyingId === "credit_topup" ? "Redirecting..." : "Buy Top-up"}
              </Button>
            </div>
          </div>
        )}

        <p className="text-xs text-zinc-600 mt-10 text-center">
          Free plan includes 15 credits every month, refreshed automatically.{" "}
          <button data-testid="billing-back-to-binder-link" onClick={() => navigate("/binder")} className="text-[#A78BFA] hover:underline">Back to your binder</button>
        </p>
      </div>
    </div>
  );
}
